import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useState, useEffect } from 'react';
import {
    Download,
    PenLine,
    Share2,
    Send,
    Calendar,
    MapPin,
    User,
    AlertCircle,
    Loader2,
    CheckSquare,
    Square,
} from 'lucide-react';
import { BackButton } from '@/components/ui/app/back-button';
import { PageHeader } from '@/pages/_partials/mobile-headers';
import { Button } from '@/components/ui/button';
import { SubmitButton } from '@/components/ui/app/submit-button';
import { PhotoThumbnail, getMediaDetailPath } from '@/components/ui/PhotoGallery';
import { useProcedureReport } from '@/lib/hooks/use-procedure-report';
import { useSafeBack } from '@/lib/hooks/use-safe-back';
import { useApiCache } from '@/lib/contexts/api-cache-context';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { toast } from 'sonner';
import FormReportSend, { FORM_REPORT_SEND_ID } from './_partials/forms/FormReportSend';
import { usePermissions } from '@/lib/hooks/use-permissions';
import { useTranslation } from 'react-i18next';
import i18n from '@/lib/i18n';

/**
 * Grille de photos avec navigation vers la page de détail
 */
function PhotoFieldGrid({ photos, folderId, interventionId, navigate, sourceUrl }) {
    const handlePhotoClick = (photo) => {
        // URL canonique: /pictures/{id} pour image, /videos/{id} pour vidéo
        const path = getMediaDetailPath(photo);
        if (path) {
            navigate(path, { state: { from: sourceUrl } });
        }
    };

    return (
        <div className="flex flex-wrap gap-[8px]">
            {photos.map((photo, photoIndex) => (
                <PhotoThumbnail
                    key={photo.uniq_id || photoIndex}
                    photo={photo}
                    index={photoIndex}
                    showRemoveButton={false}
                    showHorodateBadge={true}
                    onClick={photo.uniq_id ? () => handlePhotoClick(photo) : undefined}
                    width={200}
                    height={200}
                />
            ))}
        </div>
    );
}

/**
 * Formate une date ISO en format français (DD/MM/YYYY)
 */
function formatDate(dateString) {
    if (!dateString) return '';
    try {
        const date = new Date(dateString);
        return date.toLocaleDateString('fr-FR');
    } catch {
        return dateString;
    }
}

/**
 * Formate une date ISO en heure (HH:MM)
 */
function formatTime(dateString) {
    if (!dateString) return '';
    try {
        const date = new Date(dateString);
        return date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
    } catch {
        return '';
    }
}

/**
 * Transforme les données du rapport de procédure pour l'affichage
 */
function transformReportData(report) {
    if (!report) return null;

    const procedureReport = report.procedureReport;
    const templateVersion = procedureReport?.procedureTemplateVersion;
    const configuration = templateVersion?.configuration || procedureReport?.configuration;
    const answers = procedureReport?.answers;
    const documents = report.documents || [];

    // Créer une map des documents par uniq_id pour un accès rapide
    const documentsMap = {};
    documents.forEach((doc) => {
        documentsMap[doc.uniq_id] = doc;
    });

    // Construire la liste des sections avec leurs champs et valeurs
    const sections = (configuration?.sections || []).map((section, sectionIndex) => {
        const answerSection = answers?.sections?.find((as) => as.id === section.id) || {};

        const fields = (section.fields || []).map((field) => {
            const answerField = answerSection.fields?.find((af) => af.id === field.id);

            // Pour les champs photo/vidéo, récupérer les documents liés
            let photos = [];
            if (
                (field.type === 'photo' ||
                    field.type === 'photo_certified' ||
                    field.type === 'video_certified') &&
                Array.isArray(answerField?.value)
            ) {
                photos = answerField.value.map((docId) => documentsMap[docId]).filter(Boolean);
            }

            return {
                id: field.id,
                label: field.label || field.name || i18n.t('procedures:champ'),
                value: answerField?.value ?? '',
                type: field.type,
                options: field.options || [], // Options pour les champs checkbox/select
                photos, // Documents liés à ce champ photo
            };
        });

        return {
            id: section.id || String(sectionIndex + 1),
            title: section.name || section.title || `Section ${sectionIndex + 1}`,
            icon: section.icon || '📋',
            color: section.color || '#6366F1',
            fields,
        };
    });

    return {
        id: report.uniq_id,
        title: report.name || i18n.t('procedures:rapportDeProcedure'),
        date: formatDate(report.intervention_date || report.created_at),
        time: formatTime(report.intervention_date || report.created_at),
        location:
            report.address ||
            (report.latitude && report.longitude ? `${report.latitude}, ${report.longitude}` : i18n.t('procedures:nonSpecifiee')),
        realizedBy: report.user_name || i18n.t('procedures:nonSpecifie'),
        sections,
    };
}

export default function ProcedureReport() {
    const { t } = useTranslation();
    const { procedureId, reportId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const { goBack } = useSafeBack();
    const { setReportReturnTo, getReportReturnTo } = useApiCache();
    const { can } = usePermissions();
    const [isSendSheetOpen, setIsSendSheetOpen] = useState(false);
    const [isSending, setIsSending] = useState(false);
    const [canSend, setCanSend] = useState(false);

    // Stocker le returnTo au mount si ça pointe vers /folders, /procedures (page liste) ou /search
    useEffect(() => {
        const returnTo = location.state?.returnTo;
        if (returnTo) {
            // On stocke seulement si c'est une page dossier, procédure (pas un rapport) ou recherche
            const isFoldersPage = returnTo.startsWith('/folders');
            const isProceduresPage = returnTo.startsWith('/procedures') && !returnTo.includes('/reports/');
            const isSearchPage = returnTo.startsWith('/search');
            if (isFoldersPage || isProceduresPage || isSearchPage) {
                setReportReturnTo(returnTo);
            }
        }
    }, [location.state?.returnTo, setReportReturnTo]);

    const handleSendReport = async (emails) => {
        if (!folderId || !interventionId) return;

        try {
            setIsSending(true);
            await window.axios.post(`/api/folders/${folderId}/interventions/${interventionId}/report/send-email`, {
                emails,
            });
            toast.success(t('procedures:rapportEnvoyeAvecSucces'));
            setIsSendSheetOpen(false);
        } catch (error) {
            console.error('Error sending report:', error);
            toast.error(t('procedures:erreurLorsDeLEnvoiDuRapport'));
        } finally {
            setIsSending(false);
        }
    };

    // Handler de retour contextuel
    const handleBack = () => {
        const returnTo = getReportReturnTo();
        if (returnTo) {
            navigate(returnTo);
        } else {
            goBack();
        }
    };

    // URL actuelle pour la navigation retour depuis les photos
    const currentUrl = location.pathname;

    // Récupération du rapport depuis l'API
    const { report, isLoading, error, notFound, refetch } = useProcedureReport(procedureId, reportId);

    // Extraire folderId et interventionId pour la navigation vers les photos
    const folderId = report?.folder_uniq_id;
    const interventionId = report?.uniq_id;

    // Transformer les données pour l'affichage
    const reportData = transformReportData(report);

    // État de chargement
    const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);

    const handleGeneratePdf = async () => {
        if (!folderId || !interventionId) return;

        try {
            setIsGeneratingPdf(true);
            const response = await window.axios.post(
                `/api/folders/${folderId}/interventions/${interventionId}/procedure-report-file`,
                {},
                { responseType: 'blob' },
            );

            // Récupérer le nom du fichier depuis le header content-disposition si disponible
            let filename = 'rapport.pdf';
            const disposition = response.headers['content-disposition'];
            if (disposition && disposition.indexOf('attachment') !== -1) {
                const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
                const matches = filenameRegex.exec(disposition);
                if (matches != null && matches[1]) {
                    filename = matches[1].replace(/['"]/g, '');
                }
            } else if (disposition && disposition.indexOf('inline') !== -1) {
                const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
                const matches = filenameRegex.exec(disposition);
                if (matches != null && matches[1]) {
                    filename = matches[1].replace(/['"]/g, '');
                }
            }

            // Utiliser File au lieu de Blob pour tenter de préserver le nom
            const file = new File([response.data], filename, { type: 'application/pdf' });
            const url = window.URL.createObjectURL(file);

            // Créer un lien temporaire pour déclencher le téléchargement
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', filename); // Forcer le nom du fichier
            document.body.appendChild(link);
            link.click();

            // Nettoyage
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Error generating PDF:', error);
        } finally {
            setIsGeneratingPdf(false);
        }
    };

    if (isLoading) {
        return (
            <div className="bg-surface relative size-full overflow-hidden flex flex-col">
                <PageHeader title={t('procedures:chargement')}>
                    <BackButton onClick={handleBack} />
                </PageHeader>
                <div className="flex-1 flex items-center justify-center">
                    <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                </div>
            </div>
        );
    }

    // État d'erreur
    if (error || notFound) {
        return (
            <div className="bg-surface relative size-full overflow-hidden flex flex-col">
                <PageHeader title={t('procedures:erreur')}>
                    <BackButton onClick={handleBack} />
                </PageHeader>
                <div className="flex-1 flex flex-col items-center justify-center px-6">
                    <AlertCircle className="w-12 h-12 text-destructive mb-4" />
                    <p className="text-muted-foreground text-center mb-4">
                        {notFound ? t('procedures:rapportNonTrouve') : t('procedures:erreurLorsDuChargementDuRapport')}
                    </p>
                    <Button variant="outline" color="neutral" onClick={() => refetch()}>
                        {t('procedures:reessayer')}
                    </Button>
                </div>
            </div>
        );
    }

    // Rapport non trouvé après chargement
    if (!reportData) {
        return (
            <div className="bg-surface relative size-full overflow-hidden flex flex-col">
                <PageHeader title={t('procedures:nonTrouve')}>
                    <BackButton onClick={handleBack} />
                </PageHeader>
                <div className="flex-1 flex flex-col items-center justify-center px-6">
                    <p className="text-muted-foreground">{t('procedures:rapportNonTrouve')}</p>
                    <Button variant="outline" color="neutral" onClick={handleBack} className="mt-4">
                        {t('procedures:retour')}
                    </Button>
                </div>
            </div>
        );
    }

    return (
        <div className="bg-surface relative size-full overflow-hidden flex flex-col">
            {/* Header */}
            <PageHeader
                title={t('procedures:rapportDeProcedure')}
                actions={
                    <>
                        {report?.procedureReport?.status !== 'completed' && !report?.read_only && can('UPDATE_PROCEDURE_REPORTS') && (
                            <Button
                                variant="solid"
                                color="neutral"
                                size="xmd"
                                onClick={() => navigate(`/procedures/${procedureId}/reports/${reportId}/edit`)}
                                className="flex items-center justify-center h-[36px] px-[12px] rounded-[10px] transition-colors"
                            >
                                <PenLine className="h-4 w-4 text-primary-foreground" strokeWidth={2.5} />
                                <span className="hidden sm:inline text-[13px] text-primary-foreground ml-[6px]">
                                    {t('procedures:modifier')}
                                </span>
                            </Button>
                        )}
                        {report?.procedureReport?.status === 'completed' && (
                            <>
                                <Button
                                    variant="plain"
                                    color="neutral"
                                    size="xmd"
                                    onClick={handleGeneratePdf}
                                    disabled={isGeneratingPdf}
                                    className="flex items-center justify-center h-[36px] w-[36px] sm:w-auto sm:px-[12px] rounded-[10px] bg-muted hover:bg-border transition-colors shrink-0 disabled:opacity-50"
                                    aria-label={t('procedures:telecharger')}
                                >
                                    {isGeneratingPdf ? (
                                        <Loader2 className="h-4 w-4 text-muted-foreground animate-spin" />
                                    ) : (
                                        <Download className="h-4 w-4 text-muted-foreground" strokeWidth={2.5} />
                                    )}
                                    <span className="hidden sm:inline text-[13px] text-muted-foreground ml-[6px]">
                                        {t('procedures:telechargerLeRapport')}
                                    </span>
                                </Button>
                                <Button
                                    variant="plain"
                                    color="neutral"
                                    size="xmd"
                                    onClick={() => setIsSendSheetOpen(true)}
                                    className="flex items-center justify-center h-[36px] w-[36px] sm:w-auto sm:px-[12px] rounded-[10px] bg-muted hover:bg-border transition-colors shrink-0 disabled:opacity-50"
                                    aria-label={t('procedures:envoyerParEmail')}
                                >
                                    <Send className="h-4 w-4 text-black" strokeWidth={2.5} />
                                    <span className="hidden sm:inline text-[13px] text-muted-foreground ml-[6px]">
                                        {t('procedures:envoyerParEmail')}
                                    </span>
                                </Button>
                            </>
                        )}
                    </>
                }
            >
                <BackButton onClick={handleBack} />
            </PageHeader>

            {/* Content */}
            <div className="flex-1 overflow-y-auto scrollbar-hide pb-[30px]">
                <div className="px-[20px] md:px-0 pt-[20px] md:max-w-[62.5%] md:min-w-[728px] mx-auto">
                    {/* Report Header Card */}
                    <div className="bg-card rounded-[16px] p-[20px] mb-[15px] border border-border">
                        <div className="flex items-start justify-between gap-[12px] mb-[15px]">
                            <h2 className=" text-[20px] text-foreground">{reportData.title}</h2>
                            {report?.procedureReport?.status === 'draft' && (
                                <span className="inline-flex items-center gap-[6px] bg-surface px-[10px] py-[4px] rounded-full shrink-0">
                                    <span className="w-1.5 h-1.5 rounded-full bg-muted-foreground" />
                                    <span className="text-[12px] text-muted-foreground">{t('procedures:brouillon')}</span>
                                </span>
                            )}
                        </div>
                        <div className="space-y-[10px]">
                            <div className="flex items-center gap-[8px]">
                                <Calendar className="h-4 w-4 text-muted-foreground" />
                                <span className=" text-[13px] text-muted-foreground">
                                    {reportData.date} {reportData.time && `- ${reportData.time}`}
                                </span>
                            </div>
                            {reportData.location && reportData.location !== 'Non spécifiée' && (
                                <div className="flex items-center gap-[8px]">
                                    <MapPin className="h-4 w-4 text-muted-foreground" />
                                    <span className=" text-[13px] text-muted-foreground">{reportData.location}</span>
                                </div>
                            )}
                            <div className="flex items-center gap-[8px]">
                                <User className="h-4 w-4 text-muted-foreground" />
                                <span className=" text-[13px] text-muted-foreground">
                                    {t('procedures:realisePar')} {reportData.realizedBy}
                                </span>
                            </div>
                        </div>
                    </div>

                    {/* Sections */}
                    {reportData.sections.map((section) => (
                        <div
                            key={section.id}
                            className="bg-card rounded-[16px] p-[20px] mb-[15px] border border-border"
                        >
                            {/* Section Header */}
                            <div className="flex items-center gap-[12px] mb-[15px]">
                                <div className="w-[32px] h-[32px] bg-surface rounded-full flex items-center justify-center text-[18px]">
                                    {section.icon}
                                </div>
                                <h3 className=" text-[16px] text-foreground flex-1">{section.title}</h3>
                            </div>

                            {/* Fields */}
                            <div className="space-y-[15px]">
                                {section.fields.map((field, index) => (
                                    <div key={field.id || index}>
                                        <label className=" text-[12px] text-muted-foreground mb-[6px] block">
                                            {field.label}
                                        </label>

                                        {/* Champ photo/vidéo - afficher les médias */}
                                        {field.type === 'photo' ||
                                        field.type === 'photo_certified' ||
                                        field.type === 'video_certified' ? (
                                            field.photos && field.photos.length > 0 ? (
                                                <PhotoFieldGrid
                                                    photos={field.photos}
                                                    folderId={folderId}
                                                    interventionId={interventionId}
                                                    navigate={navigate}
                                                    sourceUrl={currentUrl}
                                                />
                                            ) : (
                                                <div className="bg-surface rounded-[12px] px-[14px] py-[12px] border border-border">
                                                    <p className=" text-[14px] text-muted-foreground italic">
                                                        {field.type === 'video_certified'
                                                            ? t('procedures:aucuneVideo')
                                                            : t('procedures:aucunePhoto')}
                                                    </p>
                                                </div>
                                            )
                                        ) : field.type === 'checkbox' && field.options?.length > 0 ? (
                                            /* Champ checkbox - afficher toutes les options avec état coché/non coché */
                                            <div className="bg-surface rounded-[12px] px-[14px] py-[12px] border border-border space-y-[8px]">
                                                {field.options.map((option, optIndex) => {
                                                    const isChecked = Array.isArray(field.value)
                                                        ? field.value.includes(option)
                                                        : field.value === option;
                                                    return (
                                                        <div key={optIndex} className="flex items-center gap-[8px]">
                                                            {isChecked ? (
                                                                <CheckSquare className="h-4 w-4 text-secondary" />
                                                            ) : (
                                                                <Square className="h-4 w-4 text-muted-foreground" />
                                                            )}
                                                            <span
                                                                className={`text-[14px] ${isChecked ? 'text-foreground' : 'text-muted-foreground'}`}
                                                            >
                                                                {option}
                                                            </span>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        ) : field.type === 'date' ? (
                                            /* Champ date - formater comme dans l'en-tête */
                                            <div className="bg-surface rounded-[12px] px-[14px] py-[12px] border border-border">
                                                <p className="text-[14px] text-foreground">
                                                    {field.value ? (
                                                        formatDate(field.value)
                                                    ) : (
                                                        <span className="text-muted-foreground italic">
                                                            {t('procedures:nonRenseigne')}
                                                        </span>
                                                    )}
                                                </p>
                                            </div>
                                        ) : field.type === 'signature' ? (
                                            /* Champ signature - afficher l'image */
                                            <div className="bg-surface rounded-[12px] px-[14px] py-[12px] border border-border">
                                                {field.value ? (
                                                    <div className="flex items-center gap-[12px]">
                                                        <img
                                                            src={field.value}
                                                            alt={t('procedures:signature')}
                                                            className="max-h-[120px] w-auto rounded-[8px] border border-border bg-card"
                                                        />
                                                        <div className="flex items-center gap-[6px] text-secondary">
                                                            <CheckSquare className="h-4 w-4" />
                                                            <span className="text-[12px] font-medium">{t('procedures:signe')}</span>
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <p className="text-[14px] text-muted-foreground italic">
                                                        {t('procedures:nonSigne')}
                                                    </p>
                                                )}
                                            </div>
                                        ) : (
                                            /* Champ texte/autre - afficher la valeur */
                                            <div className="bg-surface rounded-[12px] px-[14px] py-[12px] border border-border">
                                                <p className=" text-[14px] text-foreground">
                                                    {Array.isArray(field.value)
                                                        ? field.value.join(', ')
                                                        : field.value || (
                                                              <span className="text-muted-foreground italic">
                                                                  {t('procedures:nonRenseigne')}
                                                              </span>
                                                          )}
                                                </p>
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    ))}

                    {/* Empty state for sections */}
                    {reportData.sections.length === 0 && (
                        <div className="bg-card rounded-[16px] p-[40px] border border-border text-center">
                            <p className="text-muted-foreground">{t('procedures:aucuneSectionDansCeRapport')}</p>
                        </div>
                    )}
                </div>
            </div>

            {/* Send Report Sheet */}
            <Sheet open={isSendSheetOpen} onOpenChange={setIsSendSheetOpen}>
                <SheetContent>
                    <SheetHeader>
                        <SheetTitle>{t('procedures:envoyerLeRapport')}</SheetTitle>
                    </SheetHeader>
                    <div className="mt-4">
                        <FormReportSend
                            report={reportData}
                            onSubmit={handleSendReport}
                            onEmailsChange={(emails) => setCanSend(emails.length > 0)}
                        />
                    </div>
                    <div className="mt-6 flex justify-end">
                        <SubmitButton
                            isSubmitting={isSending}
                            loadingText={t('procedures:envoyer')}
                            form={FORM_REPORT_SEND_ID}
                            disabled={!canSend || isSending}
                        >
                            <Send className="mr-2 h-4 w-4" />
                            {t('procedures:envoyer')}
                        </SubmitButton>
                    </div>
                </SheetContent>
            </Sheet>
        </div>
    );
}
